import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { EquipmentCard } from "./EquipmentCard";
import { EquipmentForm } from "./EquipmentForm";

const Stat = ({ label, value, testid, tone = "" }) => (
  <div className="card-surface p-4" data-testid={testid}><div className="text-xs text-muted-foreground">{label}</div><div className={`text-2xl font-extrabold ${tone}`}>{value}</div></div>
);

export const BizEquipment = ({ company }) => {
  const qc = useQueryClient();
  const key = ["biz-equipment", company.id];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => api.get(`/business/companies/${company.id}/equipment`).then((r) => r.data) });
  const [form, setForm] = useState(null);
  const canEdit = ["director", "accountant"].includes(company.my_role);
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  if (isLoading) return <Skeleton className="h-64 rounded-2xl" />;
  const s = data.summary;
  return (
    <div className="space-y-6" data-testid="biz-equipment">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Équipements" value={s.count} testid="eq-stat-count" />
        <Stat label="Valeur d'achat" value={s.value.toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })} testid="eq-stat-value" />
        <Stat label="Sous garantie" value={s.warranty_active} testid="eq-stat-warranty" tone={s.warranty_soon ? "text-warning" : ""} />
        <Stat label="Entretiens à prévoir" value={s.maintenance_due} testid="eq-stat-maintenance" tone={s.maintenance_overdue ? "text-destructive" : ""} />
      </div>
      {canEdit && <Button className="rounded-full" onClick={() => setForm({})} data-testid="eq-add-btn"><Plus className="mr-1 h-4 w-4" />Ajouter un équipement</Button>}
      {data.items.length ? (
        <ul className="stagger grid gap-4 md:grid-cols-2 xl:grid-cols-3">{data.items.map((i) => <EquipmentCard key={i.id} company={company} item={i} canEdit={canEdit} onEdit={(x) => setForm({ item: x })} onChange={refresh} />)}</ul>
      ) : (
        <div className="card-surface flex flex-col items-center gap-2 p-10 text-center" data-testid="eq-empty"><Wrench className="h-8 w-8 text-muted-foreground" />
          <p className="font-bold">Aucun équipement enregistré</p><p className="text-sm text-muted-foreground">Ajoutez votre matériel pour suivre garanties, entretiens et réparateurs.</p></div>
      )}
      {form && <EquipmentForm key={form.item?.id || "new"} company={company} item={form.item} open onOpenChange={(o) => !o && setForm(null)} onSaved={() => { setForm(null); refresh(); }} />}
    </div>
  );
};
